/**
 * LICENSE CONTEXT
 *
 * Provides license validation state throughout the application.
 * Subscribes to the licenseManager event emitter to catch 402 responses
 * from any Axios interceptor and set the license as invalid.
 *
 * State:
 * - isLicenseValid: boolean (defaults to true, set to false on 402)
 * - licenseMessage: string|null (message from API 402 response)
 *
 * Usage:
 *   const { isLicenseValid, licenseMessage } = useLicense()
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { onLicenseRequired } from '../utils/licenseManager'

const LicenseContext = createContext()

/**
 * LicenseProvider - Wraps the app and provides license state.
 * Subscribes to licenseManager events from Axios interceptors.
 */
export const LicenseProvider = ({ children }) => {
  // Default to true — assume license is valid until API says otherwise
  const [isLicenseValid, setIsLicenseValid] = useState(true)
  const [licenseMessage, setLicenseMessage] = useState(null)

  /**
   * Handler called when any API interceptor detects a 402 response.
   * Sets license state to invalid and stores the message for display.
   */
  const handleLicenseRequired = useCallback((message) => {
    setIsLicenseValid(false)
    setLicenseMessage(message || 'A valid license is required to use this application.')
  }, [])

  // Subscribe to license-required events from Axios interceptors
  useEffect(() => {
    const unsubscribe = onLicenseRequired(handleLicenseRequired)
    return unsubscribe
  }, [handleLicenseRequired])

  return (
    <LicenseContext.Provider value={{ isLicenseValid, licenseMessage }}>
      {children}
    </LicenseContext.Provider>
  )
}

/**
 * Hook to access license validation state.
 * Must be used within a LicenseProvider.
 */
export const useLicense = () => {
  const context = useContext(LicenseContext)
  if (!context) {
    throw new Error('useLicense must be used within LicenseProvider')
  }
  return context
}
